import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Group,
  SegmentedControl,
  Select,
  Slider,
  Stack,
  Switch,
  Text,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { BarcodeScanner } from "../BarcodeScanner";
import {
  MAX_SIDES,
  RESOLUTIONS,
  SCANNER_DEFAULTS,
  useScannerPrefs,
  type Facing,
  type FocusMode,
} from "../../scanner/scannerPrefs";

type Caps = MediaTrackCapabilities & {
  torch?: boolean;
  zoom?: { min: number; max: number; step?: number };
  focusMode?: string[];
  focusDistance?: { min: number; max: number };
};

/** Camera select values: "auto:<facing>" or "dev:<deviceId>". */
function cameraValue(deviceId: string, facing: Facing): string {
  return deviceId ? `dev:${deviceId}` : `auto:${facing}`;
}

export function ScannerSettingsTab() {
  const { t } = useTranslation();
  const narrow = useMediaQuery("(max-width: 48em)");
  const [prefs, update] = useScannerPrefs();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [testing, setTesting] = useState(false);
  const [caps, setCaps] = useState<Caps | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);

  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === "videoinput"));
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    void refreshDevices();
  }, [refreshDevices]);

  const onTrackReady = useCallback(
    (track: MediaStreamTrack) => {
      try {
        setCaps((track.getCapabilities?.() ?? {}) as Caps);
      } catch {
        setCaps({});
      }
      // Labels are only exposed once camera permission has been granted.
      void refreshDevices();
    },
    [refreshDevices],
  );

  const cameraOptions = [
    { value: "auto:environment", label: t("scannerSettings.cameraAutoBack") },
    { value: "auto:user", label: t("scannerSettings.cameraAutoFront") },
    ...devices
      .filter((d) => d.deviceId)
      .map((d, i) => ({
        value: `dev:${d.deviceId}`,
        label: d.label || t("scannerSettings.cameraN", { n: i + 1 }),
      })),
  ];
  const selectedCamera = cameraValue(prefs.deviceId, prefs.facing);
  // A saved camera that this browser no longer lists still shows as selected.
  if (!cameraOptions.some((o) => o.value === selectedCamera)) {
    cameraOptions.push({ value: selectedCamera, label: t("scannerSettings.cameraSaved") });
  }

  const zoomCaps = caps?.zoom;
  const torchSupported = caps ? !!caps.torch : null;
  const manualFocusSupported = caps
    ? !!(caps.focusMode?.includes("manual") && caps.focusDistance)
    : null;
  const unsupported = t("scannerSettings.unsupported");
  const testFirst = t("scannerSettings.testFirst");

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text fw={600}>{t("scannerSettings.camera")}</Text>
        <Select
          data={cameraOptions}
          value={selectedCamera}
          allowDeselect={false}
          onChange={(v) => {
            if (!v) return;
            if (v.startsWith("dev:")) update({ deviceId: v.slice(4) });
            else update({ deviceId: "", facing: v.slice(5) as Facing });
            setCaps(null);
          }}
        />
        <Text size="xs" c="dimmed">
          {t("scannerSettings.cameraHint")}
        </Text>
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("scannerSettings.preview")}</Text>
        <Group>
          <Button variant={testing ? "light" : "filled"} onClick={() => setTesting((v) => !v)}>
            {testing ? t("scannerSettings.stopTest") : t("scannerSettings.startTest")}
          </Button>
          {lastCode && (
            <Text size="sm">
              {t("scannerSettings.lastCode")}: <b>{lastCode}</b>
            </Text>
          )}
        </Group>
        {testing && (
          <div style={{ maxWidth: 480 }}>
            <BarcodeScanner
              active
              onDetect={(raw) => setLastCode(raw)}
              dedupeMs={500}
              onTrackReady={onTrackReady}
            />
          </div>
        )}
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("scannerSettings.resolution")}</Text>
        <SegmentedControl
          fullWidth={!!narrow}
          value={String(prefs.resolution)}
          onChange={(v) => update({ resolution: Number(v) })}
          data={RESOLUTIONS.map((r) => ({ value: String(r), label: `${r}p` }))}
        />
        <Text size="sm">{t("scannerSettings.maxSide")}</Text>
        <SegmentedControl
          fullWidth={!!narrow}
          value={String(prefs.maxSide)}
          onChange={(v) => update({ maxSide: Number(v) })}
          data={MAX_SIDES.map((m) => ({ value: String(m), label: `${m}px` }))}
        />
        <Text size="sm">
          {t("scannerSettings.quality")}: {Math.round(prefs.quality * 100)}%
        </Text>
        <Slider
          min={0.4}
          max={0.95}
          step={0.05}
          value={prefs.quality}
          onChange={(v) => update({ quality: v })}
          label={(v) => `${Math.round(v * 100)}%`}
        />
        <Text size="xs" c="dimmed">
          {t("scannerSettings.qualityHint")}
        </Text>
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("scannerSettings.speed")}</Text>
        <Text size="sm">
          {t("scannerSettings.interval", {
            ms: prefs.intervalMs,
            fps: (1000 / prefs.intervalMs).toFixed(1),
          })}
        </Text>
        <Slider
          min={100}
          max={1000}
          step={50}
          value={prefs.intervalMs}
          onChange={(v) => update({ intervalMs: v })}
          label={(v) => `${v} ms`}
        />
        <Text size="sm">{t("scannerSettings.timeout", { s: (prefs.timeoutMs / 1000).toFixed(1) })}</Text>
        <Slider
          min={1000}
          max={10000}
          step={500}
          value={prefs.timeoutMs}
          onChange={(v) => update({ timeoutMs: v })}
          label={(v) => `${(v / 1000).toFixed(1)} s`}
        />
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("scannerSettings.hardware")}</Text>
        {!caps && (
          <Alert variant="light" color="gray" p="xs">
            <Text size="xs">{testFirst}</Text>
          </Alert>
        )}
        <Switch
          label={t("scannerSettings.torch")}
          description={torchSupported === false ? unsupported : undefined}
          checked={prefs.torch}
          disabled={torchSupported === false}
          onChange={(e) => update({ torch: e.currentTarget.checked })}
        />
        <Text size="sm">
          {t("scannerSettings.zoom")}:{" "}
          {prefs.zoom > 0 ? `${prefs.zoom.toFixed(1)}×` : t("scannerSettings.zoomDefault")}
          {caps && !zoomCaps ? ` — ${unsupported}` : ""}
        </Text>
        <Slider
          min={0}
          max={zoomCaps ? zoomCaps.max : 10}
          step={zoomCaps?.step && zoomCaps.step > 0 ? zoomCaps.step : 0.1}
          value={prefs.zoom}
          disabled={!!caps && !zoomCaps}
          onChange={(v) => update({ zoom: zoomCaps && v > 0 && v < zoomCaps.min ? zoomCaps.min : v })}
          label={(v) => (v > 0 ? `${v.toFixed(1)}×` : t("scannerSettings.zoomDefault"))}
        />
        <Text size="sm">{t("scannerSettings.focus")}</Text>
        <SegmentedControl
          fullWidth={!!narrow}
          value={prefs.focus}
          onChange={(v) => update({ focus: v as FocusMode })}
          data={[
            { value: "continuous", label: t("scannerSettings.focusContinuous") },
            { value: "manual", label: t("scannerSettings.focusManual") },
          ]}
        />
        {manualFocusSupported === false && (
          <Text size="xs" c="dimmed">
            {t("scannerSettings.focusManual")}: {unsupported}
          </Text>
        )}
        {prefs.focus === "manual" && (
          <>
            <Text size="sm">
              {t("scannerSettings.focusDistance")}: {Math.round(prefs.focusDistance * 100)}%
            </Text>
            <Slider
              min={0}
              max={1}
              step={0.01}
              value={prefs.focusDistance}
              onChange={(v) => update({ focusDistance: v })}
              label={(v) => `${Math.round(v * 100)}%`}
            />
          </>
        )}
        <Text size="xs" c="dimmed">
          {t("scannerSettings.tapToFocus")}
        </Text>
      </Stack>

      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {t("appearance.perBrowser")}
        </Text>
        <Button variant="default" onClick={() => update(SCANNER_DEFAULTS)}>
          {t("scannerSettings.reset")}
        </Button>
      </Group>
    </Stack>
  );
}
